import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import {
  type ImagingInstance,
  RXDB_IMAGING_INSTANCE_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/imaging-instance";
import {
  type ImagingStudy,
  RXDB_IMAGING_STUDY_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/imaging-study";
import { gql, useApolloClient } from "@apollo/client";
import { useCallback, useEffect, useState } from "react";
import { useRxData } from "rxdb-hooks";
import { ImagingAssetKind } from "../../../../../../__generated__/base.gql";
import { GenerateImagingAssetUrlDocument } from "./__generated__/use-patient-imaging-studies.gql";

gql`
  query GenerateImagingAssetUrl($input: ImagingAssetUrlInput!) {
    generateImagingAssetUrl(input: $input) {
      url
    }
  }
`;

export const usePatientImagingStudies = (patientId: string) => {
  const [imagingStudySyncFlag] = useFeatureFlag(
    featureFlags.pocus.imagingStudySync,
  );
  const apolloClient = useApolloClient();
  const [hydratedStudies, setHydratedStudies] = useState<
    Array<{
      id: string;
      startedAt: string;
      accessionNumber: string;
      images: Array<{
        id: string;
        thumbnailUrl?: string;
        previewUrl?: string;
        thumbnailLoading?: boolean;
        previewLoading?: boolean;
      }>;
      meta: {
        created: string;
        createdAt: string;
        createdByUserId: string;
        updatedAt: string;
        updatedByUserId: string;
      };
    }>
  >([]);
  const [isLoading, setIsLoading] = useState(false);

  const getAssetUrl = useCallback(
    async (
      imagingInstanceId: string,
      kind: ImagingAssetKind,
    ): Promise<string | null> => {
      try {
        const result = await apolloClient.query({
          query: GenerateImagingAssetUrlDocument,
          variables: { input: { imagingInstanceId, kind } },
          fetchPolicy: "cache-first",
        });

        return result.data?.generateImagingAssetUrl?.url || null;
      } catch (error) {
        console.error(
          `Failed to generate ${kind} URL for ${imagingInstanceId}:`,
          error,
        );
        return null;
      }
    },
    [apolloClient],
  );

  const { result: imagingStudies } = useRxData<ImagingStudy>(
    RXDB_IMAGING_STUDY_COLLECTION_NAME,
    imagingStudySyncFlag
      ? (collection) =>
          collection.find({
            selector: { elephantPatientId: { $eq: patientId } },
            sort: [{ "meta.updatedAt": "desc" }],
          })
      : undefined,
  );

  const { result: imagingInstances } = useRxData<ImagingInstance>(
    RXDB_IMAGING_INSTANCE_COLLECTION_NAME,
    imagingStudySyncFlag
      ? (collection) =>
          collection.find({
            selector: { elephantPatientId: { $eq: patientId } },
            sort: [{ "meta.updatedAt": "desc" }],
          })
      : undefined,
  );

  const getInstancesForStudy = useCallback(
    (accessionNumber: string): ImagingInstance[] => {
      return imagingInstances
        .filter(
          (instance) =>
            instance.imagingStudyAccessionNumber === accessionNumber,
        )
        .sort((a, b) => a.id.localeCompare(b.id));
    },
    [imagingInstances],
  );

  useEffect(() => {
    let isCancelled = false;

    const processImages = async () => {
      if (!imagingStudySyncFlag || imagingStudies.length === 0) {
        if (!isCancelled) {
          setHydratedStudies([]);
          setIsLoading(false);
        }
        return;
      }

      if (!isCancelled) {
        setIsLoading(true);
      }

      try {
        const initialStudies = imagingStudies.map((study) => {
          const studyInstances = getInstancesForStudy(study.accessionNumber);

          const images = studyInstances.map((instance) => ({
            id: instance.id,
            thumbnailUrl: undefined,
            previewUrl: undefined,
            thumbnailLoading: true,
            previewLoading: true,
          }));

          return {
            id: study.id,
            startedAt: study.meta.createdAt,
            accessionNumber: study.accessionNumber,
            images,
            meta: {
              ...study.meta,
              created: study.meta.createdAt,
            },
          };
        });

        if (!isCancelled) {
          setHydratedStudies(initialStudies);
        }

        const studyUpdates = imagingStudies.map(async (study) => {
          const studyInstances = getInstancesForStudy(study.accessionNumber);

          const imageUpdates = studyInstances.map(async (instance) => {
            const [thumbnailUrl, previewUrl] = await Promise.all([
              getAssetUrl(instance.id, ImagingAssetKind.Thumbnail),
              getAssetUrl(instance.id, ImagingAssetKind.Preview),
            ]);

            if (!isCancelled) {
              setHydratedStudies((prevStudies) => {
                return prevStudies.map((prevStudy) => {
                  if (prevStudy.accessionNumber !== study.accessionNumber) {
                    return prevStudy;
                  }

                  const updatedImages = prevStudy.images.map((prevImage) => {
                    if (prevImage.id !== instance.id) {
                      return prevImage;
                    }

                    return {
                      ...prevImage,
                      thumbnailUrl: thumbnailUrl || undefined,
                      previewUrl: previewUrl || undefined,
                      thumbnailLoading: false,
                      previewLoading: false,
                    };
                  });

                  return {
                    ...prevStudy,
                    images: updatedImages,
                  };
                });
              });
            }
          });

          await Promise.all(imageUpdates);
        });

        await Promise.all(studyUpdates);

        if (!isCancelled) {
          setIsLoading(false);
        }
      } catch (error) {
        if (!isCancelled) {
          console.error("Failed to process imaging studies:", error);
          setIsLoading(false);
        }
      }
    };

    processImages();

    return () => {
      isCancelled = true;
    };
  }, [
    imagingStudies,
    imagingInstances,
    getAssetUrl,
    imagingStudySyncFlag,
    getInstancesForStudy,
  ]);

  return {
    studies: hydratedStudies.filter((study) => study.images.length > 0),
    isLoading,
  };
};

export type ImagingStudyWithImages = ReturnType<
  typeof usePatientImagingStudies
>["studies"][0];
